/**
 * A DOM text field overlaid on the canvas.
 *
 * Real `<input>` elements are used for anything the player types (usernames,
 * passwords, lobby codes) because hand-rolled canvas text entry cannot match
 * IMEs, mobile keyboards, paste, or the accessibility tree.
 */

export interface TextFieldStyle {
  background?: string;
  colour?: string;
  border?: string;
  fontSize?: string;
}

export class TextEntry {
  private input: HTMLInputElement;
  private container: HTMLElement;
  private active = false;
  private submitHandler: ((value: string) => void) | null = null;
  private changeHandler: ((value: string) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    const input = document.createElement('input');
    input.type = 'text';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.style.position = 'absolute';
    input.style.display = 'none';
    input.style.margin = '0';
    input.style.padding = '0 10px';
    input.style.borderRadius = '8px';
    input.style.outline = 'none';
    input.style.fontFamily = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';
    input.style.zIndex = '40';
    input.style.boxSizing = 'border-box';
    input.setAttribute('aria-label', 'Text input');

    input.addEventListener('input', () => {
      this.changeHandler?.(input.value);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submitHandler?.(input.value);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.blur();
      }
      // Stop the game's global handlers from stealing typing keys.
      e.stopPropagation();
    });

    container.appendChild(input);
    this.input = input;
  }

  get isActive(): boolean {
    return this.active;
  }

  get value(): string {
    return this.input.value;
  }

  /**
   * Shows and focuses the field. `rect` is in canvas CSS pixels relative to the
   * canvas element, which must be positioned at the container's origin.
   */
  focus(
    rect: { x: number; y: number; w: number; h: number },
    options: {
      value?: string;
      placeholder?: string;
      password?: boolean;
      maxLength?: number;
      style?: TextFieldStyle;
      onChange?: (value: string) => void;
      onSubmit?: (value: string) => void;
    } = {},
  ): void {
    this.active = true;
    this.changeHandler = options.onChange ?? null;
    this.submitHandler = options.onSubmit ?? null;
    this.input.type = options.password ? 'password' : 'text';
    this.input.placeholder = options.placeholder ?? '';
    this.input.maxLength = options.maxLength ?? 64;
    if (this.input.value !== (options.value ?? '')) this.input.value = options.value ?? '';
    const style = options.style ?? {};
    this.input.style.background = style.background ?? '#1f232d';
    this.input.style.color = style.colour ?? '#eae8e0';
    this.input.style.border = style.border ?? '1px solid #2b303d';
    this.input.style.fontSize = style.fontSize ?? '14px';
    this.position(rect);
    this.input.style.display = 'block';
    // Focus after layout so mobile browsers scroll to the right place.
    requestAnimationFrame(() => {
      if (this.active) this.input.focus({ preventScroll: true });
    });
  }

  position(rect: { x: number; y: number; w: number; h: number }): void {
    const canvas = this.container.querySelector('canvas');
    const offsetX = canvas ? canvas.offsetLeft : 0;
    const offsetY = canvas ? canvas.offsetTop : 0;
    this.input.style.left = `${Math.round(rect.x + offsetX)}px`;
    this.input.style.top = `${Math.round(rect.y + offsetY)}px`;
    this.input.style.width = `${Math.round(rect.w)}px`;
    this.input.style.height = `${Math.round(rect.h)}px`;
    this.input.style.lineHeight = `${Math.round(rect.h)}px`;
  }

  blur(): void {
    if (!this.active) return;
    this.active = false;
    this.submitHandler = null;
    this.changeHandler = null;
    this.input.style.display = 'none';
    this.input.blur();
  }

  dispose(): void {
    this.blur();
    this.input.remove();
  }
}
